/* eslint-disable no-restricted-properties */
import { ReactElement } from 'react';
import 'whatwg-fetch';
import get from 'lodash/get';
import camelCase from 'lodash/camelCase';
import lowerCase from 'lodash/lowerCase';

import { accountTypes, browserHistory, endpoint, hostnames, systemErrorCodes } from 'shared/constants';
import { logDebug, getPackageVersion } from 'shared/functions/utils';

import rocketImage from 'assets/images/grow-rocket.png';
import errorField from 'assets/images/error.png';

import { ResponseDataWithErrorsType, ResponseErrorType } from './types';

const microserviceEndpoints = [
  'authorisation',
  'authentication',
  'job-requests',
  'business-workflows',
  'undp-custom',
  'static-pages',
  'bi',
];

export const updateFirstName = ({ firstName }) => (firstName ? (localStorage.firstName = firstName) : '');

const parseJSON = <R,>(response: Response) => response.json() as Promise<R>;

export const showQuotaPopup = (message: ReactElement) =>
  window.setNotificationComponent(
    // TODO move to shared/ui as independent component
    <div>
      <div className="clearfix" />
      <img
        src={rocketImage}
        alt="Time to grow"
        style={{
          maxWidth: '200px',
          marginTop: '-20px',
        }}
      />
      <h3>{translate("It's Time to Grow!")}</h3>
      <div>
        <p className="text-black margin-bottom">{message || 'Quota exceeded'}</p>
        <p
          className="small"
          style={{
            lineHeight: '1.5em',
          }}
        >
          To learn more about how you can continue using EVA, please contact a member of our team to upgrade your
          account
        </p>
      </div>
    </div>,
  );

export const showNylasSyncPopup = (message: ReactElement) =>
  window.setNotificationComponent(
    // TODO:move to shared/ui as independent component
    <div>
      <div className="margin-top">
        <div>
          <img
            src={errorField}
            alt=""
            style={{
              maxWidth: '110px',
              marginTop: '-10px',
            }}
          />
          <h3 className="text-danger">Email and calendar synchronisation error</h3>
          <div className="text-black margin-bottom">{message}</div>
          <div
            className="small margin-top"
            style={{
              lineHeight: '1.5em',
            }}
          >
            <div>
              Synchronize your email <a href="/agent/user-profile">here</a>.
            </div>
          </div>
        </div>
      </div>
      <p className="small margin-top">
        <a href="mailto:hello@eva.ai">hello@eva.ai</a>
      </p>
    </div>,
    true,
  );

const checkStatus = (response: Response) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const message = response.statusText || `Error status: ${response.status}; Error url: ${response.url}`;
  const error: ResponseErrorType = new Error(message) as ResponseErrorType;
  error.response = response;

  if (response.text) {
    return response.text().then((text) => {
      let json: ResponseDataWithErrorsType | string;
      try {
        json = JSON.parse(text);
      } catch (err) {
        error.payload = { error: text };
        return Promise.reject(error);
      }

      const errors = (typeof json === 'object' && json.errors) || [];
      const systemErrors = errors.filter((item) => item.code && systemErrorCodes.includes(item.code));

      if (Array.isArray(systemErrors) && systemErrors.length) {
        const quotaErrors = errors.filter((item) => item.code === 'system.quota-exceeded');
        const nylasDisabledErrors = errors.filter((item) =>
          ['system.nylas-sync-error', 'system.nylas-sync-disabled'].includes(item.code),
        );

        if (quotaErrors.length) {
          showQuotaPopup(get(quotaErrors, '[0].message', ''));
          if (typeof json === 'object') {
            json.handled = true;
          }
        } else if (nylasDisabledErrors.length) {
          showNylasSyncPopup(get(nylasDisabledErrors, '[0].message', ''));
          if (typeof json === 'object') {
            json.handled = true;
          }
        }
      }

      error.status = response.status;

      error.payload =
        typeof json === 'object'
          ? json
          : {
              error: `${json}`,
            };

      return Promise.reject(error);
    });
  }

  throw error;
};

const unauthenticatedStatus = 401;
const tryToUpdateFirstName = () => {
  try {
    const { userType } = JSON.parse(localStorage.user);
    if (!window.isAgent && userType !== accountTypes.agent) {
      updateFirstName(JSON.parse(localStorage.user));
    }
  } catch (err) {
    // do nothing;
  }
};
const checkAuth = (response: Response) => {
  if (response.status === unauthenticatedStatus) {
    if (['token', 'user'].some((key) => localStorage[key] !== undefined)) {
      tryToUpdateFirstName();
      delete localStorage.token;
      delete localStorage.user;
      window.location.reload();
    }
  }

  if (response.status === 302) {
    const url = response.headers.get('x-location');
    if (url) {
      browserHistory.pushPatched(url);
    }
  }

  return response;
};

export const fetchRequest = async (url: string, { method = 'GET', ...options }: RequestInit = {}) => {
  const headers = {
    'accept-version': getPackageVersion(),
    'Content-Type': 'application/json',
    authorization: localStorage.token ? `Bearer ${localStorage.token}` : undefined,
    ...(options.headers || {}),
  };

  const fetchOptions = {
    method,
    ...options,
    headers,
  };

  const catchError = (err: ResponseErrorType) => {
    err.url = url;
    err.text = err.message; // NOTE because JSON.stringify don't show err.message

    if (err.name === 'AbortError') {
      throw new Error('AbortError', { cause: err });
    }

    if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
      throw new Error('NetworkError', { cause: err });
    }

    throw err;
  };

  try {
    return await fetch(url, fetchOptions);
  } catch (err) {
    if (lowerCase(method) === 'get' && err.message === 'TypeError: Failed to fetch') {
      logDebug(`'Got TypeError: Failed to fetch, try call again', url: ${url}, fetchOptions: ${fetchOptions}`);

      try {
        return await fetch(url, fetchOptions);
      } catch (err) {
        catchError(err);
      }
    }

    catchError(err);
  }
};
/* @deprecated TODO remove in DEV-25601 use shared/api/{service}/api instead */
export const request = <R,>(url: string, options: RequestInit) =>
  fetchDataWithChecks(url, options).then((response: Response) => parseJSON<R>(response));

export const fetchDataWithChecks = (url: string, options: RequestInit) =>
  fetchRequest(url, options).then(checkAuth).then(checkStatus);

export const getServerUrl = (url: string) => {
  let serverUrl = endpoint;
  const { tenant = 'dev' } = localStorage;
  const checkStorageVariable = (variableName: string) => {
    if (localStorage[variableName]) {
      serverUrl = `http://localhost:${localStorage[variableName]}/api`;
    } else if (window.location.hostname === hostnames.local) {
      serverUrl = `https://${tenant}.api.eva.ai/api`;
    }
  };

  microserviceEndpoints.forEach((microserviceEndpoint) => {
    if (url.startsWith(`/${microserviceEndpoint}`)) {
      checkStorageVariable(`${camelCase(microserviceEndpoint)}Port`);
    }
  });

  return `${serverUrl}${url}`;
};
/* @deprecated TODO remove in DEV-25601 use shared/api/{service}/api instead */
export const requestBackend = <T,>(url: string, options?: RequestInit) => request<T>(getServerUrl(url), options);
/* @deprecated TODO remove in DEV-25601 use shared/api/{service}/api instead */
export const requestBackendWithChecks = (url: string, options?: RequestInit) =>
  fetchDataWithChecks(getServerUrl(url), options);
// eslint-disable-next-line import/no-default-export
export default request;
